import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { apartmentsApi, type Apartment, type UpdateApartmentData, type Building, type ApartmentGroup, type ApartmentUsageType } from '../../api/apartmentsApi';
import { formatDateToDisplay, parseDisplayDateToIso } from '../../utils/apartmentDate';
import { isValidEmailList } from '../../utils/emailList';

const EditApartment: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [usageTypes, setUsageTypes] = useState<ApartmentUsageType[]>([]);
  const [apartment, setApartment] = useState<Apartment | null>(null);
  
  const [formData, setFormData] = useState<UpdateApartmentData>({
    id: '',
    code: '',
    name: '',
    occupancyStatus: undefined,
    handoverDate: '',
    peCode: '',
    populationCount: undefined,
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    invoiceEmail: '',
    address: '',
    area: undefined,
    taxCode: '',
    notes: '',
    hasVatInvoice: false,
    buildingId: '',
    groupId: '',
    usageTypeId: '',
    registeredQuotaCount: undefined,
  });

  useEffect(() => {
    if (id) {
      loadApartment();
      loadBuildings();
    }
  }, [id]);

  const loadApartment = async () => {
    if (!id) return;
    
    try {
      setLoadingData(true);
      const data = await apartmentsApi.getApartmentById(id);
      setApartment(data);
      setFormData({
        id: data.id,
        code: data.code,
        name: data.name || '',
        occupancyStatus: data.occupancyStatus,
        handoverDate: formatDateToDisplay(data.handoverDate),
        peCode: data.peCode || '',
        populationCount: data.populationCount,
        ownerName: data.ownerName,
        ownerPhone: data.ownerPhone || '',
        ownerEmail: data.ownerEmail || '',
        invoiceEmail: data.invoiceEmail || '',
        address: data.address || '',
        area: data.area,
        taxCode: data.taxCode || '',
        notes: data.notes || '',
        hasVatInvoice: data.hasVatInvoice || false,
        buildingId: data.buildingId,
        groupId: data.groupId || '',
        usageTypeId: data.usageTypeId || '',
        registeredQuotaCount: data.registeredQuotaCount,
      });
      loadUsageTypes(data.buildingId);
      loadApartmentGroups(data.buildingId);
    } catch (err) {
      setError(t('errorLoadingApartments'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
    } catch (err) {
      setError(t('failedToLoad'));
    }
  };

  const loadApartmentGroups = async (buildingId?: string) => {
    if (!buildingId) {
      setApartmentGroups([]);
      return;
    }
    try {
      const data = await apartmentsApi.getApartmentGroups(buildingId);
      setApartmentGroups(data);
    } catch (err) {
      console.error('Failed to load apartment groups:', err);
      setApartmentGroups([]);
    }
  };

  const loadUsageTypes = async (buildingId?: string) => {
    if (!buildingId) {
      setUsageTypes([]);
      return;
    }
    try {
      const data = await apartmentsApi.getApartmentUsageTypes(buildingId);
      setUsageTypes(data);
    } catch (err) {
      console.error('Failed to load usage types:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.code?.trim()) {
      setError(t('pleaseEnterApartmentCode'));
      return;
    }
    if (!formData.ownerName?.trim()) {
      setError(t('pleaseEnterOwnerName'));
      return;
    }
    if (!formData.buildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }
    if (!isValidEmailList(formData.ownerEmail)) {
      setError(t('invalidEmailList'));
      return;
    }
    if (!isValidEmailList(formData.invoiceEmail)) {
      setError(t('invalidEmailList'));
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        groupId: formData.groupId || undefined,
        usageTypeId: formData.usageTypeId || undefined,
        area: formData.area || undefined,
        registeredQuotaCount: formData.registeredQuotaCount ?? undefined,
        populationCount: formData.populationCount ?? undefined,
        occupancyStatus: formData.occupancyStatus || undefined,
        handoverDate: parseDisplayDateToIso(formData.handoverDate),
        name: formData.name?.trim() || undefined,
        peCode: formData.peCode?.trim() || undefined,
      };
      await apartmentsApi.updateApartment(submitData);
      alert(t('apartmentUpdatedSuccess'));
      navigate('/apartments');
    } catch (err) {
      setError(t('errorUpdatingApartment'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked 
             : name === 'area' ? (value ? parseFloat(value) : undefined)
             : name === 'registeredQuotaCount' || name === 'populationCount'
               ? (value ? parseInt(value, 10) : undefined)
             : value
    }));
  };

  const filteredGroups = formData.buildingId ? apartmentGroups : [];

  const filteredUsageTypes = usageTypes.filter(type =>
    !formData.buildingId || type.buildingId === formData.buildingId
  );

  if (loadingData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('loadingApartments')}</div>
      </div>
    );
  }

  if (!apartment) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-lg mb-4">Apartment not found</div>
        <button
          onClick={() => navigate('/apartments')}
          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors"
        >
          {t('backToList')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('editApartment')}</h1>
        <button
          onClick={() => navigate('/apartments')}
          className="text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t('back')}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - General Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-4 text-blue-600 border-b border-blue-200 pb-2">
              {t('generalInfo')}
            </h3>
            
            <div className="space-y-4">
              {/* Building Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chọn chung cư
                </label>
                <select
                  name="buildingId"
                  value={formData.buildingId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">-- Chọn chung cư --</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Apartment Group Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chọn nhóm căn hộ
                </label>
                <select
                  name="groupId"
                  value={formData.groupId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('noApartmentGroup')}</option>
                  {filteredGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="mt-2 text-sm text-red-600 hover:text-red-800 border border-red-300 px-3 py-1 rounded"
                >
                  + {t('addApartmentGroup')}
                </button>
              </div>

              {/* Apartment Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mã căn hộ
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="MÃ CĂN HỘ"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentName')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  placeholder={t('apartmentNamePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentOccupancyStatus')}
                </label>
                <select
                  name="occupancyStatus"
                  value={formData.occupancyStatus || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">--</option>
                  <option value="OWNER_USING">{t('occupancyOwnerUsing')}</option>
                  <option value="RENTED">{t('occupancyRented')}</option>
                  <option value="FOR_RENT">{t('occupancyForRent')}</option>
                  <option value="FOR_SALE">{t('occupancyForSale')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('handoverDate')}
                </label>
                <input
                  type="text"
                  name="handoverDate"
                  value={formData.handoverDate || ''}
                  onChange={handleChange}
                  placeholder={t('handoverDatePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">{t('handoverDateEmptyHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('peCode')}
                </label>
                <input
                  type="text"
                  name="peCode"
                  value={formData.peCode || ''}
                  onChange={handleChange}
                  placeholder={t('peCodePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('populationCount')}
                </label>
                <input
                  type="number"
                  name="populationCount"
                  value={formData.populationCount ?? ''}
                  onChange={handleChange}
                  placeholder={t('populationCountPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="1"
                />
              </div>

              {/* Usage Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('usageType')}
                </label>
                <select
                  name="usageTypeId"
                  value={formData.usageTypeId || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('noUsageType')}</option>
                  {filteredUsageTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({type.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Registered Quota Count */}

              {/* Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Diện tích (m2)
                </label>
                <input
                  type="number"
                  name="area"
                  value={formData.area || ''}
                  onChange={handleChange}
                  placeholder="Diện tích"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Owner Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-4 text-blue-600 border-b border-blue-200 pb-2">
              Thông tin chủ/đại diện căn hộ
            </h3>
            
            <div className="space-y-4">
              {/* Owner Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên chủ hộ
                </label>
                <input
                  type="text"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  placeholder="Tên chủ căn hộ"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Điện thoại
                </label>
                <input
                  type="tel"
                  name="ownerPhone"
                  value={formData.ownerPhone}
                  onChange={handleChange}
                  placeholder="Điện thoại chủ căn hộ"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Địa chỉ
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Địa chỉ chủ căn hộ"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email - Fee notification */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('feeNotificationEmail')}
                </label>
                <input
                  type="text"
                  name="ownerEmail"
                  value={formData.ownerEmail}
                  onChange={handleChange}
                  placeholder={t('feeNotificationEmailPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
              </div>

              {/* Invoice Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEmail')}
                </label>
                <input
                  type="text"
                  name="invoiceEmail"
                  value={formData.invoiceEmail}
                  onChange={handleChange}
                  placeholder={t('invoiceEmailPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
              </div>

              {/* Tax Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mã số thuế
                </label>
                <input
                  type="text"
                  name="taxCode"
                  value={formData.taxCode}
                  onChange={handleChange}
                  placeholder="Mã số thuế"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Thông tin ghi chú"
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* VAT Invoice Checkbox */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="hasVatInvoice"
                    checked={formData.hasVatInvoice}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Có xuất hóa đơn VAT
                  </label>
                </div>
                <p className="mt-2 text-xs text-red-600">
                  Các thông tin này được sử dụng để gửi thông tin & phát hành hóa đơn (nếu có).
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 font-medium flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>{loading ? 'Đang cập nhật...' : 'CẬP NHẬT THÔNG TIN'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditApartment; 